import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Check, Mail, Phone } from "lucide-react";
import { Eyebrow, Reveal } from "./Reveal";
import { CONTACT, DISCOUNTS } from "./content";

const FIELDS = [
  ["name", "Parent's name"],
  ["teenAge", "Teen's age"],
  ["city", "Georgia city or ZIP"],
  ["phone", "Best callback number"],
];

const buildMailto = (picked, f) => {
  const list = DISCOUNTS.filter((d) => picked.includes(d.id)).map((d) => `- ${d.title}`).join("\n") || "- Not sure yet, please review all four";
  const body = `Hi Hunt Shield Insurance,\n\nI'd like a teen driver quote and a review of the ShieldTeen Safe Driver Program.\n\nParent: ${f.name}\nTeen's age: ${f.teenAge}\nCity/ZIP: ${f.city}\nCallback: ${f.phone}\n\nDiscounts I think my teen may qualify for:\n${list}\n\nThank you!`;
  return `mailto:${CONTACT.email}?subject=${encodeURIComponent("Teen Driver Quote - ShieldTeen Program")}&body=${encodeURIComponent(body)}`;
};

const Toggle = ({ d, on, flip }) => (
  <motion.button
    type="button"
    whileTap={{ scale: 0.97 }}
    onClick={flip}
    data-testid={`stacker-toggle-${d.id}`}
    aria-pressed={on}
    className={`flex items-center justify-between gap-4 rounded-2xl border px-5 py-4 text-left transition-colors duration-300 ${on ? "border-lane bg-lane text-asphalt" : "border-white/15 bg-asphalt text-bone hover:border-white/40"}`}
  >
    <span className="font-display text-xl font-bold uppercase leading-tight">{d.title}</span>
    <span className={`grid h-7 w-7 shrink-0 place-items-center rounded-full border ${on ? "border-asphalt bg-asphalt text-lane" : "border-white/30"}`}>
      {on && <Check className="h-4 w-4" />}
    </span>
  </motion.button>
);

export const Stacker = () => {
  const [picked, setPicked] = useState([]);
  const [form, setForm] = useState({ name: "", teenAge: "", city: "", phone: "" });
  const flip = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const href = useMemo(() => buildMailto(picked, form), [picked, form]);
  return (
    <section id="stacker" className="mx-auto max-w-[1400px] px-5 pb-24 pt-4 sm:px-8 lg:pb-32" data-testid="stacker-section">
      <div className="grid gap-12 rounded-[2.5rem] border border-white/10 bg-graphite p-6 sm:p-10 lg:grid-cols-2 lg:p-14">
        <Reveal>
          <Eyebrow>Discount stacker</Eyebrow>
          <h2 className="mt-6 font-display text-5xl font-black uppercase leading-[0.92] text-bone sm:text-6xl">Stack your teen's discounts.</h2>
          <p className="mt-5 max-w-md font-body text-sm leading-relaxed text-mist sm:text-base">Tap each discount that might fit your teen. We'll turn it into a ready-to-send email, so your quote request arrives with everything we need.</p>
          <div className="mt-8" data-testid="stacker-meter">
            <div className="flex items-baseline justify-between font-mono text-xs uppercase tracking-[0.2em] text-mist">
              <span>Discounts stacked</span>
              <span className="text-lane" data-testid="stacker-count">{picked.length} / 4</span>
            </div>
            <div className="mt-3 grid grid-cols-4 gap-2">
              {DISCOUNTS.map((d, i) => (
                <motion.span key={d.id} className="h-3 rounded-full bg-white/10" animate={{ backgroundColor: i < picked.length ? "#FFB800" : "rgba(255,255,255,0.1)" }} transition={{ duration: 0.4 }} />
              ))}
            </div>
          </div>
          <div className="mt-8 grid gap-3">
            {DISCOUNTS.map((d) => (
              <Toggle key={d.id} d={d} on={picked.includes(d.id)} flip={() => flip(d.id)} />
            ))}
          </div>
        </Reveal>
        <Reveal delay={0.1} className="flex flex-col">
          <div className="grid gap-5 sm:grid-cols-2">
            {FIELDS.map(([k, label]) => (
              <label key={k} className="flex flex-col gap-2">
                <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-mist">{label}</span>
                <input
                  value={form[k]}
                  onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))}
                  data-testid={`stacker-input-${k}`}
                  className="rounded-xl border border-white/15 bg-asphalt px-4 py-3.5 font-body text-bone placeholder:text-white/30 focus:border-lane focus:outline-none"
                />
              </label>
            ))}
          </div>
          <div className="mt-8 flex-1 rounded-2xl border border-dashed border-white/15 bg-asphalt/60 p-5" data-testid="stacker-preview">
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-lane">Email preview · to {CONTACT.email}</p>
            <p className="mt-3 font-body text-sm text-bone">Subject: Teen Driver Quote - ShieldTeen Program</p>
            <p className="mt-2 font-body text-sm leading-relaxed text-mist">
              {form.name || "Parent"} · teen age {form.teenAge || "—"} · {form.city || "Georgia"} · asking about{" "}
              {picked.length ? DISCOUNTS.filter((d) => picked.includes(d.id)).map((d) => d.title).join(", ") : "all four discounts"}.
            </p>
          </div>
          <div className="mt-6 flex flex-col gap-3 sm:flex-row">
            <a href={href} data-testid="stacker-send-email-btn" className="group inline-flex flex-1 items-center justify-center gap-3 rounded-full bg-lane px-6 py-4 font-body font-bold text-asphalt transition-transform duration-300 hover:-translate-y-1">
              <Mail className="h-5 w-5 transition-transform duration-300 group-hover:-rotate-12" /> Open my email
            </a>
            <a href={CONTACT.tel} data-testid="stacker-call-btn" className="inline-flex flex-1 items-center justify-center gap-3 rounded-full border border-white/20 px-6 py-4 font-body font-semibold text-bone transition-colors duration-300 hover:border-lane hover:text-lane">
              <Phone className="h-5 w-5" /> {CONTACT.phone}
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
